import { useEffect, useState } from 'react';
import {
  Server,
  AlertTriangle,
  Activity,
  CircleCheckBig,
  ArrowDown,
  RefreshCw,
  ExternalLink,
} from 'lucide-react';
import { useDashboard } from '../hooks/useDashboard.ts';
import type { TenantComProblema } from '@/shared/types/dashboard.ts';

function formatTimeAgo(date: Date): string {
  const seconds = Math.round((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return `${seconds}s atrás`;
  const minutes = Math.round(seconds / 60);
  return `${minutes}min atrás`;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
}

// ── Cards de métricas ───────────────────────────────────────

interface MetricCardProps {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  borderColor: string;
  badge?: { show: boolean; color: string };
}

function MetricCard({ label, value, icon, borderColor, badge }: MetricCardProps) {
  return (
    <div
      className={`relative rounded-lg border-l-4 bg-white p-5 shadow-sm dark:bg-gray-900 ${borderColor}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
          <p className="mt-1 text-2xl font-bold">{value}</p>
        </div>
        <div className="text-gray-400 dark:text-gray-500">{icon}</div>
      </div>
      {badge?.show && (
        <span
          className={`absolute right-3 top-3 inline-flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold text-white ${badge.color}`}
        >
          !
        </span>
      )}
    </div>
  );
}

// ── Tabela de problemas ─────────────────────────────────────

function StatusBadge({ status }: { status: TenantComProblema['status'] }) {
  if (status === 'DOWN') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium" style={{ background: 'var(--color-status-down)', color: '#fff' }}>
        <ArrowDown size={12} />
        DOWN
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium" style={{ background: 'var(--color-status-degraded)', color: '#fff' }}>
      <AlertTriangle size={12} />
      INCIDENTE
    </span>
  );
}

function ProblemaTable({ tenants }: { tenants: TenantComProblema[] }) {
  if (tenants.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <CircleCheckBig size={48} style={{ color: 'var(--color-status-up)' }} />
        <p className="mt-3 text-lg font-medium text-gray-700 dark:text-gray-300">
          Todos os sistemas operacionais
        </p>
        <p className="mt-1 text-sm text-gray-400">
          Nenhum tenant com problemas no momento.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-xs uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
            <th className="px-4 py-3">Nome</th>
            <th className="px-4 py-3">Subdomínio</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Último Check</th>
            <th className="px-4 py-3 text-right">Uptime Mês</th>
            <th className="px-4 py-3 text-center">Detalhes</th>
          </tr>
        </thead>
        <tbody>
          {tenants.map((t) => (
            <tr
              key={t.id}
              className="border-b border-gray-100 dark:border-gray-800"
            >
              <td className="px-4 py-3 font-medium">{t.nome}</td>
              <td className="px-4 py-3 font-mono text-xs text-gray-500 dark:text-gray-400">
                {t.slug}.woli.com.br
              </td>
              <td className="px-4 py-3">
                <StatusBadge status={t.status} />
              </td>
              <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                {formatDateTime(t.ultimoCheck)}
              </td>
              <td className="px-4 py-3 text-right font-mono">
                {t.uptimeMes != null ? `${t.uptimeMes}%` : '—'}
              </td>
              <td className="px-4 py-3 text-center">
                <a
                  href={`/uptime?host=${t.slug}.woli.com.br`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950"
                >
                  <ExternalLink size={12} />
                  Ver
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Página principal ────────────────────────────────────────

export function DashboardPage() {
  const { data, dataUpdatedAt, isLoading, isError, refetch } = useDashboard();
  const [now, setNow] = useState(Date.now());

  // Atualizar relógio "Atualizado há X segundos" a cada 5s
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(interval);
  }, []);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <RefreshCw size={24} className="animate-spin text-gray-400" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-2 text-gray-500">
        <AlertTriangle size={32} />
        <p>Erro ao carregar dados do dashboard.</p>
        <button
          onClick={() => refetch()}
          className="text-sm text-blue-600 underline hover:text-blue-800"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  const updatedAgo = dataUpdatedAt ? formatTimeAgo(new Date(dataUpdatedAt)) : '';

  // Suprimir warning de `now` não utilizado diretamente — usado para recalcular updatedAgo
  void now;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">Dashboard</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Visão geral do monitoramento de uptime em tempo real</p>
        </div>
        {updatedAgo && (
          <span className="flex items-center gap-1.5 text-xs text-gray-400">
            <RefreshCw size={12} />
            Atualizado há {updatedAgo}
          </span>
        )}
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          label="Tenants Ativos"
          value={data.totalTenants}
          icon={<Server size={24} />}
          borderColor="border-blue-500"
        />
        <MetricCard
          label="Tenants DOWN"
          value={data.tenantsDown}
          icon={<ArrowDown size={24} />}
          borderColor={data.tenantsDown > 0 ? 'border-[var(--color-status-down)]' : 'border-gray-300 dark:border-gray-700'}
          badge={{ show: data.tenantsDown > 0, color: 'bg-[var(--color-status-down)]' }}
        />
        <MetricCard
          label="Incidentes Abertos"
          value={data.incidentesAbertos}
          icon={<AlertTriangle size={24} />}
          borderColor={data.incidentesAbertos > 0 ? 'border-[var(--color-status-down)]' : 'border-gray-300 dark:border-gray-700'}
          badge={{ show: data.incidentesAbertos > 0, color: 'bg-[var(--color-status-down)]' }}
        />
        <MetricCard
          label="Uptime Médio (mês)"
          value={`${data.uptimeMedioMes}%`}
          icon={<Activity size={24} />}
          borderColor={
            data.uptimeMedioMes >= 99
              ? 'border-[var(--color-status-up)]'
              : data.uptimeMedioMes >= 95
                ? 'border-[var(--color-status-degraded)]'
                : 'border-[var(--color-status-down)]'
          }
        />
      </div>

      {/* Tabela de tenants com problema */}
      <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
        <div className="border-b border-gray-200 px-5 py-3 dark:border-gray-800">
          <h2 className="text-sm font-semibold">Tenants com Problema</h2>
        </div>
        <ProblemaTable tenants={data.tenantsComProblema} />
      </div>
    </div>
  );
}
